import type { BallState, MatchState, PlayerState } from './state';
import type { SimConfig } from '../config/simConfig';
import type { SimEvent } from './events';
import type { ContactEvent } from '../physics/collide';
import type { ActionKind } from '../types';
import { rngStep } from '../math/rng';
import { armReceptionGrace, claimBarred, shieldedFromBehind } from './possession';
import { gkHolding } from './ballActions';

export function isTackleAction(a: ActionKind): boolean {
  return a === 'TACKLE_STAND' || a === 'TACKLE_SLIDE';
}

/** Is this player in its active dispossess window (§5.4: STAND ticks 2–5, SLIDE ticks 3–10)? */
function inTackleWindow(p: PlayerState, cfg: SimConfig): boolean {
  const t = cfg.actions.tackle;
  if (p.action === 'TACKLE_STAND') return p.actionTicks >= t.STAND_WINDOW[0] && p.actionTicks <= t.STAND_WINDOW[1];
  if (p.action === 'TACKLE_SLIDE') return p.actionTicks >= t.SLIDE_WINDOW[0] && p.actionTicks <= t.SLIDE_WINDOW[1];
  return false;
}

/** Per-action dispossess reach (D17): the committal slide reaches farther than the standing poke. */
function reachFor(p: PlayerState, cfg: SimConfig): number {
  return p.action === 'TACKLE_SLIDE' ? cfg.actions.tackle.SLIDE_REACH : cfg.actions.tackle.STAND_REACH;
}

/**
 * D121 — a tackle connects ONCE. Retire the active dispossess window the instant it plays the ball.
 *
 * Before D120 this was implicit: a clean tackle made the tackler the possessor and the next tick's
 * `b.possessor === p.id` bail ended it. D120 made that test permanently false, so a live window
 * (STAND ticks 2–9) re-played the ball EVERY tick it came back into reach — poke, someone claims,
 * poke, spill, claim, poke: seven possession changes out of one 10-tick action. The client re-arms
 * its ball blend on every possessor change, which is what made the ball visibly lurch.
 *
 * STAND ends outright — the lunge is spent, and he is free to chase what he poked. That also means a
 * connected tackle never reaches the DUR-end whiff branch, which exists to punish a tackle that MISSED
 * (it tested the same dead `possessor === p.id` proxy, so D120 was faceplanting successful tacklers).
 * SLIDE keeps sliding and still pays the full SLIDE_RECOVERY; only its window closes.
 */
function retireTackle(p: PlayerState, cfg: SimConfig): void {
  if (p.action === 'TACKLE_STAND') {
    p.action = 'IDLE';
    p.actionTicks = 0;
  } else if (p.action === 'TACKLE_SLIDE') {
    p.actionTicks = cfg.actions.tackle.SLIDE_WINDOW[1] + 1;
  }
}

function ballInReach(state: MatchState, p: PlayerState, cfg: SimConfig): boolean {
  const b = state.ball;
  return b.pos.z < cfg.possession.CLAIM_Z && Math.hypot(b.pos.x - p.pos.x, b.pos.y - p.pos.y) <= reachFor(p, cfg);
}

/**
 * Annotate tackle-involving ContactEvents with `ballFirst` (§7.1): true iff the tackler had the ball
 * within its dispossess reach at contact. A tackle that hits the victim first (ball out of reach) ⇒
 * ballFirst=false ⇒ the foul path (M5). Non-tackle contacts are left untouched.
 */
export function annotateTackleContacts(state: MatchState, contacts: ContactEvent[], cfg: SimConfig): void {
  for (const c of contacts) {
    const tacklerId = isTackleAction(c.aAction) ? c.a : isTackleAction(c.bAction) ? c.b : -1;
    if (tacklerId < 0) continue;
    const t = state.players[tacklerId];
    c.ballFirst = !!t && ballInReach(state, t, cfg);
  }
}

/**
 * Ball-first dispossession (§5.4 / §5.6): a player whose active tackle window reaches the ball takes
 * possession, transferring it from any current carrier. Runs before the possession keep/claim step.
 * Mutates `state`; emits POSSESSION on transfer.
 */
export function resolveTackleDispossession(state: MatchState, cfg: SimConfig, events: SimEvent[]): void {
  // players iterate ascending EntityId; the FIRST eligible tackler wins and we stop — one transfer,
  // one event, lowest-id on simultaneous ties (consistent with the §5.6 claim tie-break).
  // D18: a ball held by a keeper (GK_HOLD) is in-hands — untackleable
  const holder = state.ball.possessor !== null ? state.players[state.ball.possessor] : undefined;
  if (holder && holder.action === 'GK_HOLD') return;
  for (const p of state.players) {
    if (p.sentOff || !inTackleWindow(p, cfg)) continue;
    if (!ballInReach(state, p, cfg)) continue;
    const b = state.ball;
    if (b.possessor === p.id) return; // already ours — nothing to transfer
    // D89 possession-gain grace: the player just cleanly dispossessed is locked out of an instant
    // re-take (claimCooldownOnly names him). Skip him — not return — so a DIFFERENT legally-placed
    // opponent may still win this tick. D119: the same predicate as the claim path, so a CONTESTED
    // ball (a D35 spill, which names nobody) now bars the tackle verb too — including the spiller,
    // whose own window used to outlive the spill cooldown and hand him the ball back a tick later.
    if (claimBarred(b, p.id)) continue;
    const carrier = b.possessor !== null ? state.players[b.possessor] : undefined;
    // D120: with no carrier there is nothing to dispossess. A tackler lunging through a LOOSE ball is
    // now a plain obstacle — TACKLE_* joined NO_TOUCH, and the D52 swept ball↔body collision already
    // deflects for exactly those actions (collide.ts). This branch used to hand him the ball outright,
    // which is how the D119 clip's slide "won" a shot in mid-flight and then owned an unplayable ball.
    if (!carrier) continue;
    {
      // D80 back-cone shield ("protect all dribblers"): a tackler inside the carrier's back cone can't
      // reach the shielded ball — the carrier's body screens it, so a steal from directly behind is
      // impossible for EVERYONE (bots + humans, §3). He must come around to the side/front. A second
      // tackler in a legal position may still win this tick, so skip (not return).
      if (carrier.team !== p.team && shieldedFromBehind(carrier.facing, carrier.pos, p.pos, cfg.possession.SHIELD_CONE_DOT)) continue;
      // D35 duel geometry: outcome quality from the approach angle. Tackler on the BALL'S side of the
      // carrier (in front, ball exposed) ⇒ clean steal. Reaching through/around the body ⇒ the ball
      // SPILLS loose (seeded squirt biased to the tackler's side) — a 50/50 race, not a free transfer.
      const cbx = b.pos.x - carrier.pos.x;
      const cby = b.pos.y - carrier.pos.y;
      const tbx = b.pos.x - p.pos.x;
      const tby = b.pos.y - p.pos.y;
      const cbL = Math.hypot(cbx, cby);
      const tbL = Math.hypot(tbx, tby);
      const dot = cbL > 1e-6 && tbL > 1e-6 ? (-tbx / tbL) * (cbx / cbL) + (-tby / tbL) * (cby / cbL) : 1;
      // SHIELD blocks ANY standing tackle (the carrier's counter-verb; TACKLE_SLIDE still beats it).
      // D131: the `dot >= CLEAN_DOT` qualifier is gone. It meant the shield stopped only the tackles
      // that would have been CLEAN steals and let every spill-quality one through — i.e. the stance
      // protected you from the outcome you would probably have survived anyway, and not from the one
      // that actually took the ball off you. "Shielding does nothing" was the honest read of that.
      // Placed before the spill branch, so a bounce still draws no rng (the goldens depend on it).
      if (carrier.action === 'SHIELD' && p.action === 'TACKLE_STAND') {
        return; // bounced off the shield — the tackle window burns out and the whiff stumble applies
      }
      if (dot < cfg.actions.duel.CLEAN_DOT) {
        // spill: the ball breaks SIDEWAYS out of the duel — along the tackler-side PERPENDICULAR of the
        // carrier's FACING (± π/2), never along carrier→tackler (D35's old base squirted THROUGH the
        // carrier's torso when tackled from behind — the "ball glitches behind me" bug). Seeded jitter.
        const { value, state: rs } = rngStep(state.rng);
        state.rng = rs;
        const perpA = carrier.facing + Math.PI / 2;
        const sideDot = Math.cos(perpA) * (p.pos.x - carrier.pos.x) + Math.sin(perpA) * (p.pos.y - carrier.pos.y);
        const perp = sideDot >= 0 ? perpA : carrier.facing - Math.PI / 2; // the side the tackler came in on
        const yaw = perp + (value * 2 - 1) * cfg.actions.duel.SPILL_JITTER_RAD;
        b.possessor = null;
        b.vel.x = Math.cos(yaw) * cfg.actions.duel.SPILL_SPEED;
        b.vel.y = Math.sin(yaw) * cfg.actions.duel.SPILL_SPEED;
        b.claimCooldownTicks = cfg.actions.duel.SPILL_COOLDOWN;
        b.claimCooldownOnly = null; // a real 50/50 — BOTH sides wait out the spill
        b.kickedBy = null;
        b.lastTouch = { entity: p.id, team: p.team, tick: state.tick };
        retireTackle(p, cfg); // D121: this window has played the ball — it does not get to play it again
        events.push({ t: 'POSSESSION', entity: null, why: 'TACKLE_SPILL' });
        return;
      }
    }
    // D120 clean win: the tackler PLAYS the ball, he does not take it. He is mid-lunge — NO_TOUCH — so
    // he could not hold it in any case; the D44 "seat it halfway to his carry point" transfer is gone
    // with the ownership it was smoothing. He knocks it clear of the man he took it off and collects it
    // like anyone else once his action retires. Winning the ball and being able to use it are the same
    // event again, which is the whole point of D120.
    //
    // Direction is carrier → tackler, so the ball ALWAYS leaves the beaten player behind — never the
    // tackler's facing, which for a chasing defender points back at his own goal. Speed is per verb.
    const dxc = p.pos.x - carrier.pos.x;
    const dyc = p.pos.y - carrier.pos.y;
    const dl = Math.hypot(dxc, dyc);
    let ux = dl > 1e-6 ? dxc / dl : Math.cos(p.facing);
    let uy = dl > 1e-6 ? dyc / dl : Math.sin(p.facing);
    // …except that a defender is normally GOAL-SIDE of the man he is tackling, so carrier → tackler
    // points at his own net exactly when it matters most. "Away from the carrier" and "not into my own
    // goal" are opposites in the common case, and only one thing satisfies both: the ball goes WIDE.
    // That is what a clearance is, and it is already this file's idiom — the D35 spill breaks sideways
    // too. Perpendicular, so it leaves the goal mouth rather than crossing it. D55 shape: own half only.
    //
    // D155: which of the two perpendiculars is chosen by the GOAL it is avoiding. It used to be chosen
    // by the touchline the tackler stood nearer, which fixes the perpendicular's y-component and leaves
    // its x-component — the only component the branch exists to control — entirely free. Off-axis (i.e.
    // in any duel that is not exactly collinear, which is the ordinary one) that sent 47% of clean
    // tackles at the tackler's own goal anyway, 22% of them HARDER than carrier → tackler would have,
    // up to 9.2 m/s of CLEAR_SPEED 11. The only test of the rule pinned the collinear case, where the
    // perpendicular is purely lateral and both choices are safe — so the bug had no coverage at all.
    const atkDir = state.attackDir[p.team]!;
    if (atkDir * p.pos.x < 0 && atkDir * ux < 0) {
      const px = -uy;
      const py = ux;
      // Collinear duel ⇒ px ≈ 0 ⇒ neither perpendicular points at the goal; fall back to the touchline
      // the tackler is on, which is deterministic at y = 0 and is the shape D55 pinned.
      const side = p.pos.y >= 0 ? 1 : -1;
      const sgn = Math.abs(px) > 1e-6 ? (atkDir * px >= 0 ? 1 : -1) : py * side >= 0 ? 1 : -1;
      ux = sgn * px;
      uy = sgn * py;
    }
    const speed = p.action === 'TACKLE_SLIDE' ? cfg.actions.tackle.CLEAR_SPEED : cfg.actions.tackle.POKE_SPEED;
    b.possessor = null;
    b.vel.x = ux * speed;
    b.vel.y = uy * speed;
    b.vel.z = 0;
    // D89, unchanged and now guarding something coherent: the beaten carrier cannot simply pick up the
    // ball that was just knocked off him. Everyone else — including the tackler's team — may race for it.
    b.claimCooldownTicks = cfg.actions.duel.POSSESSION_GRACE;
    b.claimCooldownOnly = carrier.id;
    b.kickedBy = null;
    b.lastTouch = { entity: p.id, team: p.team, tick: state.tick };
    retireTackle(p, cfg); // D121: one connect per tackle
    events.push({ t: 'POSSESSION', entity: null, why: 'TACKLE_CLEAN' });
    return;
  }
}

/**
 * D45 Item 6 — catch/trap (E). Run adjacent to resolveTackleDispossession (before the referee), so its
 * seeded band roll sits in the §7 draw order after tackle/possession draws and before referee draws.
 * NEVER produces a foul, spill, or stumble (a trap is a clean gather). Lowest EntityId wins on a tie.
 */
/**
 * D155 — will `resolveTrap` ACTUALLY run its gather for this player, this tick?
 *
 * The ONE rule, in the `claimBarred` idiom: `resolveTrap` reads it as its own guard, and `collide.ts`
 * reads it to decide whether the D52/D150 body exemption is honest. D150 established the principle —
 * "the exemption is only honest where a gathering verb will ACTUALLY run this tick" — but expressed it
 * in collide.ts as `p.action === 'TRAP'`, which is only the first of four conditions. `actionTicks` must
 * be inside `trap.WINDOW` ([1,6] of an 8-tick action, so ticks 0 and 7 gather nothing), and a player
 * `claimBarred` from the ball gathers nothing for the whole action. In those gaps the man was exempt
 * from his own capsule while no verb could run, so a contested ball simply passed through whoever
 * pressed F — the D149/D150 hole again, reached along the action's timeline rather than the height band.
 */
export function trapWillRun(state: { ball: BallState; players: readonly PlayerState[] }, p: PlayerState, cfg: SimConfig): boolean {
  const t = cfg.actions.trap;
  const b = state.ball;
  if (p.action !== 'TRAP' || p.sentOff) return false;
  if (p.actionTicks < t.WINDOW[0] || p.actionTicks > t.WINDOW[1]) return false;
  if (b.possessor === p.id) return false;
  if (gkHolding(state, p.id)) return false; // D133: hands are safe — the D36 doctrine, on all three take paths
  // D89 possession-gain grace: a trap is a "take" verb too — the just-beaten carrier can't instantly
  // re-gather the ball he was cleanly stripped of (same lock as the tackle/claim paths).
  // D119: and nobody gathers a CONTESTED ball — F was a way to opt out of the spill's 50/50.
  if (claimBarred(b, p.id)) return false;
  return true;
}

export function resolveTrap(state: MatchState, cfg: SimConfig, events: SimEvent[]): void {
  const b = state.ball;
  const t = cfg.actions.trap;
  const zMax = cfg.possession.GATHER_Z; // D149: named — the same ceiling collide.ts now reads
  for (const p of state.players) {
    if (!trapWillRun(state, p, cfg)) continue;
    const grab = (): void => {
      b.possessor = p.id;
      b.vel.x = 0;
      b.vel.y = 0;
      b.vel.z = 0;
      // D131/PT4-P22: a trap-gather is a gather — it arms the same fresh-receiver window a claim does,
      // instead of zeroing the cooldown (which used to erase whatever protection was running).
      armReceptionGrace(b, p.id, cfg);
      b.kickedBy = null;
      b.lastTouch = { entity: p.id, team: p.team, tick: state.tick };
      events.push({ t: 'POSSESSION', entity: p.id, why: 'TRAP' });
    };

    // vs a CARRIED ball: a weak clean-side poke only — never a spill/stagger/foul (§ excludes TRAP)
    const carrier = b.possessor !== null ? state.players[b.possessor] : undefined;
    if (carrier) {
      if (carrier.team === p.team) continue;
      if (Math.hypot(b.pos.x - p.pos.x, b.pos.y - p.pos.y) > t.REACH * t.CARRIED_REACH_MUL || b.pos.z >= zMax) continue;
      // D131: the poke respects the stance. SHIELD is "the ball is safe from in front AND behind while
      // you give up half your speed" — a verb that ignored it made the tradeoff a pure loss.
      if (carrier.action === 'SHIELD') continue;
      // D82 (per D80 "protect all dribblers"): the directional gate is now the back-cone shield, NOT the
      // strict duel dot. A poke from directly behind is screened by the carrier's body (refuse); a poke
      // from the SIDE or FRONT connects — that's the buff. The old CLEAN_DOT(0.1) gate rejected side
      // pokes too (dot ≈ −0.5 at realistic chase geometry), which is why "F did nothing". Never a spill.
      if (shieldedFromBehind(carrier.facing, carrier.pos, p.pos, cfg.possession.SHIELD_CONE_DOT)) continue;
      grab();
      return;
    }

    // vs a LOOSE ball: catch below CONTROL_MAX, always deflect above DEFLECT_MIN, seeded band between
    if (b.possessor !== null) continue;
    if (Math.hypot(b.pos.x - p.pos.x, b.pos.y - p.pos.y) > t.REACH || b.pos.z >= zMax) continue;
    const speed = Math.hypot(b.vel.x, b.vel.y, b.vel.z);
    let caught: boolean;
    if (speed <= t.CONTROL_MAX) caught = true;
    else if (speed >= t.DEFLECT_MIN) caught = false;
    else {
      const { value, state: rs } = rngStep(state.rng); // one draw per band attempt (documented §7 order)
      state.rng = rs;
      caught = value < (t.DEFLECT_MIN - speed) / (t.DEFLECT_MIN - t.CONTROL_MAX); // likelier near CONTROL_MAX
    }
    if (caught) {
      grab();
    } else {
      // damped deflect AWAY from the trapper's centre (never through him); short cooldown for HIM only
      const dx = b.pos.x - p.pos.x;
      const dy = b.pos.y - p.pos.y;
      const dd = Math.hypot(dx, dy) || 1;
      const damped = speed * t.DEFLECT_DAMP;
      b.vel.x = (dx / dd) * damped;
      b.vel.y = (dy / dd) * damped;
      b.vel.z = 0;
      b.claimCooldownTicks = cfg.actions.duel.SPILL_COOLDOWN;
      b.claimCooldownOnly = p.id;
      b.kickedBy = null;
      b.lastTouch = { entity: p.id, team: p.team, tick: state.tick };
    }
    return;
  }
}
